/*!
 * accounting.js v0.4.2
 * Copyright 2014 Open Exchange Rates
 *
 * Freely distributable under the MIT license.
 * Portions of accounting.js are inspired or borrowed from underscore.js
 *
 * Full details and documentation:
 * http://openexchangerates.github.io/accounting.js/
 */
/* --- Setup --- */
// Create the local library object, to be exported or referenced globally later
const currencySettings = {
    symbol: "$",
    format: "%s%v",
    // controls output: %s = symbol, %v = value (can be object, see docs)
    decimal: ".",
    thousand: ",",
    precision: 2,
    grouping: 3 // digit grouping (not implemented yet)
};
const numberSettings = {
    precision: 0,
    grouping: 3,
    thousand: ",",
    decimal: "."
};
/**
 * Check and normalise the value of precision (must be positive integer)
 */
function checkPrecision(val, base) {
    if (val === undefined || val === null)
        return base;
    val = Math.round(Math.abs(val));
    return isNaN(val) ? base : val;
}
/**
 * Parses a format string or object and returns format obj for use in rendering
 *
 * `format` is either a string with the default (positive) format, or object
 * containing `pos` (required), `neg` and `zero` values (or a function returning
 * either a string or object)
 *
 * Either string or format.pos must contain "%v" (value) to be valid
 */
function checkCurrencyFormat(format) {
    const defaults = currencySettings.format;
    if (!format)
        format = defaults;
    // Allow function as format parameter (should return string or object):
    if (typeof format === "function")
        format = format();
    // Format can be a string, in which case `value` ("%v") must be present:
    if (typeof format === "string") {
        if (format.match("%v")) {
            // Create and return positive, negative and zero formats:
            return {
                pos: format,
                neg: format.replace("-", "").replace("%v", "-%v"),
                zero: format
            };
        }
        else {
            return {
                pos: format,
                neg: format,
                zero: format
            };
        }
    }
    else {
        if (!format || !format.pos || !format.pos.match("%v")) {
            // If defaults is a string, casts it to an object for faster checking next time:
            return typeof defaults === "string" ? {
                pos: defaults,
                neg: defaults.replace("%v", "-%v"),
                zero: defaults
            } : defaults;
        }
        else {
            return format;
        }
    }
}
/* --- API Methods --- */
/**
 * Takes a string/array of strings, removes all formatting/cruft and returns the raw float value
 * Alias: `accounting.parse(string)`
 *
 * Decimal must be included in the regular expression to match floats (defaults to
 * accounting.settings.number.decimal), so if the number uses a non-standard decimal
 * separator, provide it as the second argument.
 *
 * Also matches bracketed negatives (eg. "$ (1.99)" => -1.99)
 *
 * Doesn't throw any errors (`NaN`s become 0) but this may change in future
 */
export function unformat(value, decimal) {
    if (value === undefined || value === null)
        return 0;
    // Return the value as-is if it's already a number:
    if (typeof value === "number")
        return value;
    // Default decimal point comes from settings, but could be set to eg. "," in opts:
    decimal = decimal || numberSettings.decimal;
    // Build regex to strip out everything except digits, decimal point and minus sign:
    const regex = new RegExp("[^0-9-" + decimal + "]", "g"), unformatted = parseFloat(("" + value)
        .replace(/\((?=\d+)(.*)\)/, "-$1") // replace bracketed values with negatives
        .replace(regex, '') // strip out any cruft
        .replace(decimal, '.') // make sure decimal point is standard
    );
    // This will fail silently which may cause trouble, let's wait and see:
    return !isNaN(unformatted) ? unformatted : 0;
}
/**
 * Implementation of toFixed() that treats floats more like decimals
 *
 * Fixes binary rounding issues (eg. (0.615).toFixed(2) === "0.61") that present
 * problems for accounting- and finance-related software.
 */
function toFixed(value, precision) {
    precision = checkPrecision(precision, numberSettings.precision);
    const exponentialForm = Number(unformat(value) + 'e' + precision);
    const rounded = Math.round(exponentialForm);
    const finalResult = Number(rounded + 'e-' + precision).toFixed(precision);
    return finalResult;
}
export function formatNumber(number, precisionOrSettings, thousand, decimal) {
    number = number ?? 0;
    // Clean up number:
    number = unformat(number);
    // Build options object from second param (if object) or all params, extending defaults:
    const opts = typeof precisionOrSettings === "object" ?
        {
            minPrecision: precisionOrSettings.minPrecision ?? precisionOrSettings.precision ?? numberSettings.precision,
            precision: precisionOrSettings.precision ?? numberSettings.precision,
            thousand: precisionOrSettings.thousand ?? numberSettings.thousand,
            decimal: precisionOrSettings.decimal ?? numberSettings.decimal,
            showBlankForZero: precisionOrSettings.showBlankForZero ?? false,
        } : {
        minPrecision: precisionOrSettings ?? numberSettings.precision,
        precision: precisionOrSettings ?? numberSettings.precision,
        thousand: thousand ?? numberSettings.thousand,
        decimal: decimal ?? numberSettings.decimal,
        showBlankForZero: false,
    };
    if (number === 0 && opts.showBlankForZero)
        return "";
    // Clean up precision
    const usePrecision = checkPrecision(opts.precision, 0);
    const useMinPrecision = checkPrecision(opts.minPrecision, 0);
    // Do some calc:
    const negative = number < 0 ? "-" : "";
    const base = parseInt(toFixed(Math.abs(number || 0), usePrecision), 10) + "";
    const mod = base.length > 3 ? base.length % 3 : 0;
    // Format the number:
    const formattedNumber = negative + (mod ? base.substr(0, mod) + opts.thousand : "") +
        base.substr(mod).replace(/(\d{3})(?=\d)/g, "$1" + opts.thousand) +
        (usePrecision ? opts.decimal + toFixed(Math.abs(number), usePrecision).split('.')[1] : "");
    if (usePrecision <= useMinPrecision)
        return formattedNumber;
    const [integer, fraction] = formattedNumber.toString().split(opts.decimal);
    const fractionArray = [...fraction];
    for (let i = fractionArray.length - 1; i >= useMinPrecision; i--) {
        if (fractionArray[i] === "0")
            fractionArray.pop();
        else
            break;
    }
    return `${integer}${opts.decimal}${fractionArray.join("")}`;
}
export function formatMoney(number, symbolOrCurrencySettings, precision, thousand, decimal, format) {
    number = number ?? 0;
    // Clean up number:
    number = unformat(number);
    // Build options object from second param (if object) or all params, extending defaults:
    const opts = typeof symbolOrCurrencySettings === "object" ?
        {
            symbol: symbolOrCurrencySettings.symbol ?? currencySettings.symbol,
            minPrecision: symbolOrCurrencySettings.minPrecision ?? symbolOrCurrencySettings.precision ?? currencySettings.precision,
            precision: symbolOrCurrencySettings.precision ?? currencySettings.precision,
            thousand: symbolOrCurrencySettings.thousand ?? currencySettings.thousand,
            decimal: symbolOrCurrencySettings.decimal ?? currencySettings.decimal,
            format: symbolOrCurrencySettings.format ?? currencySettings.format,
            showBlankForZero: symbolOrCurrencySettings.showBlankForZero ?? false,
        } :
        {
            symbol: symbolOrCurrencySettings ?? currencySettings.symbol,
            minPrecision: precision ?? currencySettings.precision,
            precision: precision ?? currencySettings.precision,
            thousand: thousand ?? currencySettings.thousand,
            decimal: decimal ?? currencySettings.decimal,
            format: format ?? currencySettings.format,
            showBlankForZero: false,
        };
    if (number === 0 && opts.showBlankForZero)
        return "";
    // Check format (returns object with pos, neg and zero):
    const formats = checkCurrencyFormat(opts.format);
    // Choose which format to use for this value:
    const useFormat = number > 0 ? formats.pos : number < 0 ? formats.neg : formats.zero;
    const formattedValue = formatNumber(Math.abs(number), {
        minPrecision: checkPrecision(opts.minPrecision, 0),
        precision: checkPrecision(opts.precision, 0),
        thousand: opts.thousand,
        decimal: opts.decimal
    });
    // Return with currency symbol added:
    return useFormat.replace('%s', opts.symbol).replace('%v', formattedValue);
}
